'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import Button from '@UIKit/Button/Button';
import { ROUTES } from '@Constants/navigation.contants';
import { getStoredConsent, setConsent, subscribeToConsent } from '@/events/core/consent';
import { enableSentryReplay } from '@/events/core/sentryReplay';
import styles from './ConsentBanner.module.css';

const getConsentSnapshot = () => getStoredConsent();
// localStorage doesn't exist during SSR — render as "already decided" there
// so the banner never appears in server-rendered markup; useSyncExternalStore
// then reconciles against the real client value right after hydration
// (its documented mechanism for exactly this), with no setState call and
// no hydration-mismatch warning.
const getConsentServerSnapshot = (): ReturnType<typeof getStoredConsent> => 'granted';

/**
 * WE-115: shown to every user (not geo-targeted to EU) until they make a
 * choice. Consent Mode's *default* (deny) is already set synchronously by
 * the inline script in src/utils/head/gtag.tsx before this component even
 * mounts — this banner only handles the *update* once the user decides.
 */
export const ConsentBanner: React.FC = () => {
    const t = useTranslations();
    const stored = useSyncExternalStore(subscribeToConsent, getConsentSnapshot, getConsentServerSnapshot);

    useEffect(() => {
        // Read localStorage directly instead of trusting `stored`: during
        // hydration this effect runs in the same passive-effect pass in which
        // React only *schedules* the reconciliation away from the hardcoded
        // 'granted' SERVER snapshot — so on a first visit `stored` is still
        // 'granted' here even though the user never made a choice, and gating
        // on it enabled Replay (full session recording) for every
        // non-consented first-time visitor.
        if (getStoredConsent() === 'granted') {
            enableSentryReplay();
        }
    }, [stored]);

    // setConsent notifies the consent store synchronously, so the [stored]
    // effect above handles enabling Replay on accept, and the re-render from
    // the store update hides the banner — no local dismissed state needed.
    const choose = (state: 'granted' | 'denied') => {
        setConsent(state);
    };

    if (stored !== null) {
        return null;
    }

    return (
        <div className={styles.banner} role="dialog" aria-label={t('consentBanner.title')}>
            <p className={styles.text}>
                {t('consentBanner.text')}{' '}
                <Link href={ROUTES.PRIVACY_POLICY} className={styles.link}>
                    {t('consentBanner.linkLabel')}
                </Link>
            </p>
            <div className={styles.actions}>
                <Button variant="ghost" size="sm" onClick={() => choose('denied')}>
                    {t('consentBanner.decline')}
                </Button>
                <Button variant="primary" size="sm" onClick={() => choose('granted')}>
                    {t('consentBanner.accept')}
                </Button>
            </div>
        </div>
    );
};
